/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Avatar, CleanIconButton, Typography } from '@neo4j-ndl/react';
import classNames from 'classnames';
import { useLayoutEffect, useRef, useState } from 'react';

import type { CommonProps } from '../../_common/types';
import { ChevronDownIconOutline } from '../../icons/generated/heroIcons/ChevronDownIconOutline';
import { ChevronUpIconOutline } from '../../icons/generated/heroIcons/ChevronUpIconOutline';

type UserBubbleProps = {
  /** The content of the user bubble */
  children?: React.ReactNode;
  /** The props for the avatar */
  avatarProps?: React.ComponentProps<typeof Avatar>;
  /** The element to display after the content. Could be a CleanIconButton to edit the user bubble. */
  trailingElement?: React.ReactNode;
  /**
   * Maximum number of lines visible before the message is collapsed.
   * Set to `null` to always show the full message.
   * @default 4
   */
  maxLines?: number | null;
};

/**
 * The component is used to display the user's message in a chat.
 * It has an avatar to represent the user and a trailing element to display potential additional actions.
 *
 * @alpha - Changes to this component may be breaking.
 */
const UserBubbleComponent = ({
  avatarProps,
  trailingElement,
  children,
  maxLines = 4,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', UserBubbleProps>) => {
  const classes = classNames('ndl-ai-user-bubble', className);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isCollapseEnabled = maxLines !== null;

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (element === null || maxLines === null) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = () => {
      const lineHeight = Number.parseFloat(
        window.getComputedStyle(element).lineHeight,
      );
      if (!Number.isFinite(lineHeight)) {
        return;
      }

      setHasOverflow(element.scrollHeight > lineHeight * maxLines + 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [children, isExpanded, maxLines]);

  const expandButton = hasOverflow ? (
    <div className="ndl-ai-user-bubble-expand-wrap">
      <CleanIconButton
        className="ndl-ai-user-bubble-expand"
        size="small"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        description={isExpanded ? 'Show less' : 'Show more'}
        htmlAttributes={{
          'aria-expanded': isExpanded,
          type: 'button',
        }}
      >
        {isExpanded ? <ChevronUpIconOutline /> : <ChevronDownIconOutline />}
      </CleanIconButton>
      <>
        <div className="ndl-ai-user-bubble-expand-blur" aria-hidden="true" />
        <div className="ndl-ai-user-bubble-expand-fill" aria-hidden="true" />
      </>
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {avatarProps && (
        <Avatar
          type="image"
          {...avatarProps}
          size="small"
          shape="circle"
          className={classNames(
            'ndl-ai-user-bubble-avatar',
            avatarProps?.className,
          )}
        />
      )}
      <div className="ndl-ai-user-bubble-main">
        <Typography
          as="div"
          variant="body-medium"
          ref={contentRef}
          className={classNames('ndl-ai-user-bubble-content', {
            'ndl-ai-user-bubble-content-clamped':
              isCollapseEnabled && !isExpanded,
          })}
          style={
            isCollapseEnabled
              ? ({
                  '--ndl-ai-user-bubble-collapsed-lines': maxLines,
                } as React.CSSProperties)
              : undefined
          }
        >
          {children}
          {hasOverflow && isExpanded && (
            <span
              className="ndl-ai-user-bubble-expand-spacer"
              aria-hidden="true"
            />
          )}
        </Typography>
        {hasOverflow && (
          <div className="ndl-ai-user-bubble-toggle">{expandButton}</div>
        )}
      </div>
      <div className="ndl-ai-user-bubble-trailing">{trailingElement}</div>
    </div>
  );
};

export { UserBubbleComponent as UserBubble };
